/**
 * Derwent Links Admin — /derwent-links-admin (protected)
 *
 * Manage the public /DerwentLinks page:
 *   - Profile info (name, headline, bio, logo, contact, footer)
 *   - Optional banner block
 *   - Links: add, edit, delete, toggle on/off, mark featured, reorder (drag & drop)
 *   - Socials (Instagram / TikTok / FB / LinkedIn / YouTube URLs)
 *   - Pixels (Meta / TikTok / GA)
 *   - Live preview panel (desktop) — mimics the phone view
 *   - Analytics tab: totals + per-link + time-series + recent clicks + CSV export
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Plus, Trash2, Star, GripVertical, ExternalLink, Save,
  BarChart3, Settings, Eye, Copy, Download, ChevronLeft, Sparkles,
  Instagram, Facebook, Linkedin, Youtube, Music2, RefreshCw, BadgeCheck,
  Users, MousePointer2, TrendingDown, Lightbulb, AlertCircle, Zap,
  Smartphone, Monitor, Clock, Activity, Globe, Share2, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { cn } from '../lib/utils';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'tiktok', label: 'TikTok', Icon: Music2 },
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
  { key: 'youtube', label: 'YouTube', Icon: Youtube },
];

const ICON_CHOICES = [
  'calendar-check', 'calendar', 'file-search', 'upload-cloud',
  'repeat', 'home', 'trending-down', 'briefcase', 'landmark',
  'phone', 'mail', 'sparkles', 'star', 'check-circle-2',
];

// ─── Link row (drag-and-drop) ────────────────────────────────────────────
const LinkRow = ({ link, onChange, onDelete, onToggle, onFeature, onDragStart, onDragOver, onDrop, isDragging, isDragOver }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(link.id, e)}
      onDragOver={(e) => onDragOver(link.id, e)}
      onDrop={(e) => onDrop(link.id, e)}
      onDragEnd={() => onDragOver(null)}
      className={cn(
        'group rounded-xl border bg-white dark:bg-slate-900 transition-all',
        isDragging ? 'opacity-40' : '',
        isDragOver ? 'border-emerald-400 shadow-md ring-2 ring-emerald-200 dark:ring-emerald-900' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300',
      )}
      data-testid={`admin-link-row-${link.id}`}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0"
          aria-label="Drag to reorder"
          data-testid={`link-drag-${link.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onFeature(link.id)}
          className={cn(
            'shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
            link.featured ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-amber-500',
          )}
          title={link.featured ? 'Featured (click to unfeature)' : 'Mark as featured'}
          data-testid={`link-feature-${link.id}`}
        >
          <Star className="w-4 h-4" fill={link.featured ? 'currentColor' : 'none'} />
        </button>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 min-w-0 text-left"
          data-testid={`link-expand-${link.id}`}
        >
          <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">{link.label}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{link.url}</p>
        </button>
        <Switch
          checked={!!link.enabled}
          onCheckedChange={(v) => onToggle(link.id, v)}
          data-testid={`link-toggle-${link.id}`}
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(link.id)}
          className="text-slate-400 hover:text-rose-600 shrink-0"
          data-testid={`link-delete-${link.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-800 p-3 space-y-2.5 bg-slate-50/60 dark:bg-slate-950/40" data-testid={`link-editor-${link.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Label</Label>
              <Input
                value={link.label || ''}
                onChange={(e) => onChange(link.id, { label: e.target.value })}
                className="h-8 text-sm"
                data-testid={`link-input-label-${link.id}`}
              />
            </div>
            <div>
              <Label className="text-[11px]">URL</Label>
              <Input
                value={link.url || ''}
                onChange={(e) => onChange(link.id, { url: e.target.value })}
                className="h-8 text-sm"
                placeholder="https://... or /strategy-session or tel:0400..."
                data-testid={`link-input-url-${link.id}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Short description (optional)</Label>
            <Input
              value={link.description || ''}
              onChange={(e) => onChange(link.id, { description: e.target.value })}
              className="h-8 text-sm"
              placeholder="Shown under the button"
              data-testid={`link-input-desc-${link.id}`}
            />
          </div>
          <div>
            <Label className="text-[11px]">Cover image URL (optional)</Label>
            <Input
              value={link.cover_image_url || ''}
              onChange={(e) => onChange(link.id, { cover_image_url: e.target.value })}
              className="h-8 text-sm"
              placeholder="https://... — adds a rich card background image"
              data-testid={`link-input-cover-${link.id}`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Section / Category</Label>
              <Input
                value={link.category || ''}
                onChange={(e) => onChange(link.id, { category: e.target.value })}
                className="h-8 text-sm"
                placeholder="Get Started, Loan Solutions..."
                data-testid={`link-input-category-${link.id}`}
              />
            </div>
            <div>
              <Label className="text-[11px]">Icon</Label>
              <Select
                value={link.icon || 'sparkles'}
                onValueChange={(v) => onChange(link.id, { icon: v })}
              >
                <SelectTrigger className="h-8 text-sm" data-testid={`link-input-icon-${link.id}`}>
                  <SelectValue placeholder="Choose icon" />
                </SelectTrigger>
                <SelectContent>
                  {ICON_CHOICES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Preview panel (phone-style, modern design) ──────────────────────────
const PreviewPanel = ({ cfg }) => (
  <div className="hidden xl:block">
    <div className="sticky top-6">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1"><Eye className="w-3 h-3" /> Live Preview</p>
      <div
        className="mx-auto w-[300px] h-[620px] rounded-[36px] p-3 shadow-2xl border-8 border-slate-900"
        style={{ background: '#0A0A0F' }}
      >
        <div
          className="h-full w-full rounded-[26px] overflow-y-auto scrollbar-hide"
          style={{ background: '#0A0A0F', color: '#fff', fontFamily: "'Inter', system-ui, sans-serif" }}
        >
          {/* Hero */}
          <div
            className="relative h-[150px]"
            style={{
              background: cfg.hero_image_url
                ? `url(${cfg.hero_image_url}) center/cover`
                : 'radial-gradient(120% 90% at 50% 0%, rgba(29,27,58,0.9), rgba(10,10,15,0) 60%), linear-gradient(180deg, #1D1B3A 0%, #0A0A0F 100%)',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0A0A0F]" />
            <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/50 border border-white/15">
              <span className="text-[7px] uppercase tracking-[0.32em] text-white font-semibold">DERWENT</span>
            </div>
            {!cfg.hero_image_url && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[24px] font-bold tracking-[0.05em] text-white/[0.08] select-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>DERWENT</span>
              </div>
            )}
            {cfg.logo_url && (
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-6 w-12 h-12 rounded-full bg-[#0A0A0F] border border-white/20 flex items-center justify-center">
                <img src={cfg.logo_url} alt="" className="w-full h-full object-contain rounded-full p-1.5" style={{ filter: 'brightness(0) invert(0.94)' }} />
              </div>
            )}
          </div>
          {/* Profile */}
          <div className="pt-11 px-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <p className="text-[16px] font-bold text-white leading-none" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>{cfg.profile_name}</p>
              {cfg.verified && <BadgeCheck className="w-3.5 h-3.5 text-white" fill="#5b8cff" />}
            </div>
            {cfg.profile_handle && <p className="text-[10px] text-white/60 mt-1 font-medium">{cfg.profile_handle}</p>}
            {cfg.profile_headline && <p className="text-[8.5px] uppercase tracking-[0.24em] text-white/75 font-bold mt-2">{cfg.profile_headline}</p>}
            <p className="text-[10px] text-white/85 mt-1.5 leading-relaxed font-medium line-clamp-2">{cfg.profile_bio}</p>
            {/* Social circles */}
            <div className="mt-3 flex justify-center gap-1.5">
              {(cfg.socials || []).filter((s) => s.enabled).map((s) => (
                <div key={s.platform} className="w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <span className="text-[6px] uppercase text-white font-bold">{s.platform[0]}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Featured */}
          {(cfg.links || []).find((l) => l.enabled && l.featured) && (
            <div className="mt-4 mx-3 rounded-2xl overflow-hidden relative min-h-[100px] bg-white">
              <div className="absolute inset-0 flex flex-col justify-end p-3 text-[#0A0A0F]">
                <p className="text-[7px] uppercase tracking-[0.24em] font-bold opacity-60">Featured</p>
                <p className="text-[13px] font-bold leading-tight mt-1" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                  {(cfg.links || []).find((l) => l.enabled && l.featured)?.label}
                </p>
              </div>
            </div>
          )}
          {/* Grid preview */}
          <div className="mt-4 px-3 grid grid-cols-2 gap-2">
            {(cfg.links || []).filter((l) => l.enabled && !l.featured).slice(0, 4).map((l, i) => {
              const accents = ['#f0abfc', '#fcd34d', '#7dd3fc', '#86efac'];
              return (
                <div
                  key={l.id}
                  className="aspect-[1/1.1] rounded-2xl bg-[#141419] border border-white/10 p-2 flex flex-col justify-end relative overflow-hidden"
                  style={l.cover_image_url ? { background: `url(${l.cover_image_url}) center/cover` } : undefined}
                >
                  {!l.cover_image_url && (
                    <div className="absolute inset-0 opacity-40" style={{ background: `radial-gradient(80% 60% at 20% 20%, ${accents[i]}55, transparent 60%)` }} />
                  )}
                  <p className="relative text-[10px] font-bold text-white leading-tight" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>{l.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─── Analytics tab (detailed — mirrors the Strategy Leads analytics) ─────
const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' },
];

const SOURCE_META = {
  instagram: { label: 'Instagram', Icon: Instagram, bar: 'from-pink-500 to-rose-500' },
  tiktok:    { label: 'TikTok',    Icon: Music2,    bar: 'from-slate-700 to-slate-900' },
  facebook:  { label: 'Facebook',  Icon: Facebook,  bar: 'from-blue-500 to-blue-600' },
  linkedin:  { label: 'LinkedIn',  Icon: Linkedin,  bar: 'from-sky-600 to-sky-700' },
  youtube:   { label: 'YouTube',   Icon: Youtube,   bar: 'from-red-500 to-red-600' },
  google:    { label: 'Google',    Icon: Globe,     bar: 'from-amber-400 to-amber-500' },
  twitter_x: { label: 'X / Twitter', Icon: Share2,  bar: 'from-slate-500 to-slate-700' },
  direct:    { label: 'Direct / typed in', Icon: Link2, bar: 'from-emerald-500 to-emerald-600' },
  other:     { label: 'Other',     Icon: Globe,     bar: 'from-violet-500 to-violet-600' },
};

const DEVICE_META = {
  ios:          { label: 'iPhone / iPad', Icon: Smartphone },
  android:      { label: 'Android',       Icon: Smartphone },
  desktop:      { label: 'Desktop',       Icon: Monitor },
  other_mobile: { label: 'Other mobile',  Icon: Smartphone },
  unknown:      { label: 'Unknown',       Icon: Globe },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
};

const FunnelBar = ({ label, count, maxCount, tone, note, testId }) => {
  const pct = maxCount > 0 ? Math.max(2, (count / maxCount) * 100) : 0;
  const tones = {
    sky:     { bar: 'bg-sky-500',     text: 'text-sky-700 dark:text-sky-300' },
    violet:  { bar: 'bg-violet-500',  text: 'text-violet-700 dark:text-violet-300' },
    amber:   { bar: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300' },
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300' },
  };
  const c = tones[tone] || tones.emerald;
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">{label}</div>
        <span className={cn('text-2xl font-black tabular-nums', c.text)}>{count}</span>
      </div>
      <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all duration-500', c.bar)} style={{ width: `${pct}%` }} />
      </div>
      {note && <div className="pl-1 mt-1 text-[11px] text-slate-500 dark:text-slate-400">↓ {note}</div>}
    </div>
  );
};

// AI Coach — Claude reads the link-in-bio analytics and suggests fixes.
const DlAiCoachCard = ({ token, range }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runCoach = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${backendUrl}/api/derwent-links/analytics/insights?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      setInsights(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to generate insights');
    } finally { setLoading(false); }
  }, [token, range]);

  const priorityStyle = (p) => {
    const key = (p || 'low').toLowerCase();
    if (key === 'high') return { pill: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300 border-rose-300 dark:border-rose-800', icon: AlertCircle, bg: 'border-l-rose-500' };
    if (key === 'medium') return { pill: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 border-amber-300 dark:border-amber-800', icon: Zap, bg: 'border-l-amber-500' };
    return { pill: 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300 border-slate-300 dark:border-slate-700', icon: Lightbulb, bg: 'border-l-slate-400' };
  };

  return (
    <div className="rounded-xl border border-violet-200 dark:border-violet-900/60 p-5 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-violet-950/30 dark:via-slate-900 dark:to-fuchsia-950/20" data-testid="dl-ai-coach-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-800 dark:text-slate-100">AI Coach</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Claude reads your traffic + clicks and tells you what to change.</p>
          </div>
        </div>
        <Button size="sm" onClick={runCoach} disabled={loading}
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-md"
          data-testid="dl-run-ai-coach"
        >
          <Sparkles className={cn('w-4 h-4 mr-1', loading && 'animate-spin')} />
          {loading ? 'Analysing…' : (insights ? 'Re-analyse' : 'Analyse my page')}
        </Button>
      </div>

      {!insights && !loading && !error && (
        <div className="text-center py-6 border-2 border-dashed border-violet-200 dark:border-violet-900/60 rounded-xl bg-white/40 dark:bg-slate-900/40">
          <Sparkles className="w-7 h-7 mx-auto text-violet-400 dark:text-violet-500 mb-2" />
          <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">Click <span className="font-bold">Analyse my page</span> for AI recommendations.</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">Claude compares your link performance, traffic sources and peak hours, then suggests specific ordering, labelling and posting-time changes.</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Sparkles className="w-6 h-6 mx-auto text-violet-500 animate-pulse" />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Claude is reading your data…</p>
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-xs text-rose-800 dark:text-rose-200" data-testid="dl-ai-coach-error">
          <span className="font-semibold">Couldn&apos;t generate insights:</span> {error}
        </div>
      )}

      {insights && (
        <div data-testid="dl-ai-coach-results">
          {insights.message && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-200 mb-3">
              {insights.message}
            </div>
          )}
          {insights.headline && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-white dark:bg-slate-900 border border-violet-200 dark:border-violet-900 mb-3 shadow-sm">
              <Lightbulb className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100" data-testid="dl-ai-coach-headline">{insights.headline}</p>
            </div>
          )}
          {(insights.insights || []).length > 0 && (
            <div className="space-y-2.5">
              {(insights.insights || []).map((ins, idx) => {
                const p = priorityStyle(ins.priority);
                const Icon = p.icon;
                return (
                  <div key={idx} className={cn('bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 border-l-4 p-3 shadow-sm', p.bg)} data-testid={`dl-ai-insight-${idx}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={cn('inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border', p.pill)}>
                        <Icon className="w-2.5 h-2.5" /> {ins.priority || 'low'}
                      </span>
                      {ins.section && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{ins.section}</span>}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed"><span className="font-semibold text-slate-800 dark:text-slate-100">What we see:</span> {ins.observation}</p>
                    <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed mt-1"><span className="font-semibold text-emerald-700 dark:text-emerald-400">Try this:</span> {ins.recommendation}</p>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-between mt-3 text-[10px] text-slate-400 dark:text-slate-500">
            <span>Confidence: <span className="font-semibold">{insights.confidence}</span></span>
            {insights.generated_at && <span>Generated {fmtTime(insights.generated_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyticsTab = ({ token }) => {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/derwent-links/analytics?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setData(json);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [range, token]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/derwent-links/analytics/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'derwent-links-events.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error('Export failed'); }
  };

  const maxDay = useMemo(() => {
    if (!data?.time_series?.length) return 1;
    return Math.max(1, ...data.time_series.map((d) => Math.max(d.views, d.clicks)));
  }, [data]);

  const maxHour = useMemo(() => {
    if (!data?.hours?.length) return 1;
    return Math.max(1, ...data.hours.map((h) => h.views + h.clicks));
  }, [data]);

  const kpis = [
    { key: 'total_views', label: 'Page Views', sub: 'total loads', Icon: Eye, border: 'border-sky-200 dark:border-sky-800/60', text: 'text-sky-700 dark:text-sky-300', head: 'text-sky-600 dark:text-sky-400' },
    { key: 'unique_visitors', label: 'Unique Visitors', sub: 'distinct people', Icon: Users, border: 'border-violet-200 dark:border-violet-800/60', text: 'text-violet-700 dark:text-violet-300', head: 'text-violet-600 dark:text-violet-400' },
    { key: 'total_clicks', label: 'Link Clicks', sub: 'tile taps', Icon: MousePointer2, border: 'border-emerald-200 dark:border-emerald-800/60', text: 'text-emerald-700 dark:text-emerald-300', head: 'text-emerald-600 dark:text-emerald-400' },
    { key: 'total_social_clicks', label: 'Social Clicks', sub: 'icon taps', Icon: Share2, border: 'border-fuchsia-200 dark:border-fuchsia-800/60', text: 'text-fuchsia-700 dark:text-fuchsia-300', head: 'text-fuchsia-600 dark:text-fuchsia-400' },
    { key: 'conversion_rate', label: 'CTR', sub: 'clicks ÷ views', Icon: BarChart3, border: 'border-amber-200 dark:border-amber-800/60', text: 'text-amber-700 dark:text-amber-300', head: 'text-amber-600 dark:text-amber-400', suffix: '%' },
  ];

  return (
    <div className="space-y-5" data-testid="dl-admin-analytics">
      {/* Range selector + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Range:</span>
          <div className="flex gap-1 flex-wrap">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRange(opt.value)}
                className={cn(
                  'text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all',
                  range === opt.value
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                )}
                data-testid={`analytics-range-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="analytics-refresh">
            <RefreshCw className={cn('w-3.5 h-3.5 mr-1', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="analytics-export">
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        Showing data for <span className="font-semibold text-slate-700 dark:text-slate-200">{data?.range?.label || '—'}</span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map(({ key, label, sub, Icon, border, text, head, suffix }) => (
          <div key={key} className={cn('rounded-xl bg-white dark:bg-slate-900 border p-4', border)} data-testid={`analytics-kpi-${key}`}>
            <div className={cn('flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider', head)}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <p className={cn('text-3xl font-black mt-1 tabular-nums', text)}>{data?.[key] ?? 0}{suffix || ''}</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* AI Coach */}
      <DlAiCoachCard token={token} range={range} />

      {/* Funnel */}
      <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-funnel-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
          <TrendingDown className="w-4 h-4" /> Visitor funnel
        </h3>
        <FunnelBar label="Page Views" count={data?.total_views || 0} maxCount={data?.total_views || 1} tone="sky"
          note={`${data?.unique_visitors || 0} unique visitors`} testId="dl-funnel-views" />
        <FunnelBar label="Unique Visitors" count={data?.unique_visitors || 0} maxCount={data?.total_views || 1} tone="violet"
          note={`${data?.engagement_rate ?? 0}% went on to tap something`} testId="dl-funnel-unique" />
        <FunnelBar label="Engaged (tapped ≥1 link)" count={data?.engaged_sessions || 0} maxCount={data?.total_views || 1} tone="amber"
          note={`${data?.clicks_per_engaged ?? 0} taps per engaged visitor on average`} testId="dl-funnel-engaged" />
        <FunnelBar label="Total Taps (links + socials)" count={(data?.total_clicks || 0) + (data?.total_social_clicks || 0)} maxCount={Math.max(data?.total_views || 1, (data?.total_clicks || 0) + (data?.total_social_clicks || 0))} tone="emerald" testId="dl-funnel-taps" />
      </div>

      {/* Two-col: Top links + Traffic over time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-top-links-card">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
            <MousePointer2 className="w-4 h-4" /> Link performance
          </h3>
          {(data?.per_link || []).length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No clicks yet.</p>
          ) : (
            <div className="space-y-2.5">
              {(data?.per_link || []).slice(0, 12).map((row, i) => {
                const max = data.per_link[0]?.clicks || 1;
                const w = (row.clicks / max) * 100;
                return (
                  <div key={i} data-testid={`dl-link-perf-${i}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[60%]">{row.label}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                        <span className="font-bold text-slate-800 dark:text-slate-100">{row.clicks}</span>
                        <span className="ml-2">{row.share}% of taps</span>
                        {row.ctr != null && <span className="ml-2 text-emerald-600 dark:text-emerald-400">{row.ctr}% CTR</span>}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500" style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(data?.per_social || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">Social icon taps</p>
              <div className="flex flex-wrap gap-2">
                {data.per_social.map((s) => (
                  <span key={s.platform} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {s.platform} <span className="font-black">{s.clicks}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-timeseries-card">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Views vs clicks
          </h3>
          {(data?.time_series || []).length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No time-series data yet.</p>
          ) : (
            <>
              <div className="flex items-end gap-1 h-40 min-h-[120px]">
                {data.time_series.map((t, idx) => {
                  const vh = maxDay > 0 ? (t.views / maxDay) * 100 : 0;
                  const ch = maxDay > 0 ? (t.clicks / maxDay) * 100 : 0;
                  return (
                    <div key={t.date + idx} className="flex-1 min-w-0 flex items-end gap-0.5 h-full group" title={`${t.date}\nViews: ${t.views}\nClicks: ${t.clicks}`}>
                      <div className="flex-1 bg-sky-400/70 dark:bg-sky-500/70 rounded-t-sm min-h-[1px] group-hover:bg-sky-500" style={{ height: `${vh}%` }} />
                      <div className="flex-1 bg-emerald-500 rounded-t-sm min-h-[1px] group-hover:bg-emerald-600" style={{ height: `${ch}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                <span>{data.time_series[0]?.date}</span>
                <span>{data.time_series[data.time_series.length - 1]?.date}</span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px]">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sky-400/70" /> <span className="text-slate-600 dark:text-slate-300">Views</span></span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> <span className="text-slate-600 dark:text-slate-300">Clicks</span></span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Two-col: Traffic sources + Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-sources-card">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Traffic sources
          </h3>
          {(data?.sources || []).length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No traffic yet.</p>
          ) : (
            <div className="space-y-2.5">
              {(data?.sources || []).map((s) => {
                const meta = SOURCE_META[s.source] || SOURCE_META.other;
                const max = data.sources[0]?.views || 1;
                return (
                  <div key={s.source} data-testid={`dl-source-${s.source}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        <meta.Icon className="w-3.5 h-3.5" /> {meta.label}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                        <span className="font-bold text-slate-800 dark:text-slate-100">{s.views}</span>
                        <span className="ml-2">({s.pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className={cn('h-full rounded-full bg-gradient-to-r transition-all duration-500', meta.bar)} style={{ width: `${(s.views / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-3 leading-relaxed">
            Note: Instagram &amp; TikTok in-app browsers often hide the referrer, so some social traffic shows as &quot;Direct&quot;.
          </p>
        </div>

        <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-devices-card">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Devices
          </h3>
          {(data?.devices || []).length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No device data yet.</p>
          ) : (
            <div className="space-y-2.5">
              {(data?.devices || []).map((d) => {
                const meta = DEVICE_META[d.device] || DEVICE_META.unknown;
                const max = data.devices[0]?.views || 1;
                return (
                  <div key={d.device} data-testid={`dl-device-${d.device}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        <meta.Icon className="w-3.5 h-3.5" /> {meta.label}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                        <span className="font-bold text-slate-800 dark:text-slate-100">{d.views}</span>
                        <span className="ml-2">({d.pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-500" style={{ width: `${(d.views / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Hour-of-day strip */}
          <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Activity by hour (Hobart time)
            </p>
            <div className="flex items-end gap-[2px] h-16" data-testid="dl-hours-strip">
              {(data?.hours || []).map((h) => {
                const total = h.views + h.clicks;
                const ht = maxHour > 0 ? (total / maxHour) * 100 : 0;
                return (
                  <div key={h.hour} className="flex-1 flex flex-col items-center gap-0.5 group" title={`${String(h.hour).padStart(2, '0')}:00 — ${h.views} views, ${h.clicks} clicks`}>
                    <div className={cn('w-full rounded-t-sm min-h-[2px]', total > 0 ? 'bg-gradient-to-t from-emerald-500 to-teal-400' : 'bg-slate-100 dark:bg-slate-800')} style={{ height: `${Math.max(3, ht)}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[9px] text-slate-400 dark:text-slate-500 tabular-nums">
              <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">Post to your socials just before the peak hours to catch the wave.</p>
          </div>
        </div>
      </div>

      {/* Recent activity feed */}
      <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5" data-testid="dl-recent-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4" /> Recent activity
        </h3>
        {(data?.recent || []).length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">No taps recorded yet.</p>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
            {(data?.recent || []).map((r, i) => {
              const srcMeta = SOURCE_META[r.source] || SOURCE_META.other;
              const devMeta = DEVICE_META[r.device] || DEVICE_META.unknown;
              const isCall = (r.url || '').startsWith('tel:');
              return (
                <div key={i} className="flex items-center gap-3 py-2" data-testid={`dl-recent-${i}`}>
                  <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                    r.event_type === 'social_click' ? 'bg-fuchsia-100 dark:bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400'
                      : isCall ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                        : 'bg-sky-100 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400')}>
                    {r.event_type === 'social_click' ? <Share2 className="w-3.5 h-3.5" /> : isCall ? <Phone className="w-3.5 h-3.5" /> : <MousePointer2 className="w-3.5 h-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{r.label || 'Unknown'}</p>
                    <p className="text-[10.5px] text-slate-500 dark:text-slate-400">
                      via {srcMeta.label} · {devMeta.label}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums shrink-0">{fmtTime(r.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Admin page ─────────────────────────────────────────────────────
const DerwentLinksAdmin = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState('editor'); // editor | analytics
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const token = useMemo(() => localStorage.getItem('auth_token'), []);
  const savedRef = useRef(false);

  // Load config
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/derwent-links/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setCfg(await res.json());
      } catch (e) {
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updateCfg = (patch) => {
    setCfg((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const updateLink = (id, patch) => {
    setCfg((prev) => ({
      ...prev,
      links: prev.links.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
    setDirty(true);
  };

  const toggleLink = (id, on) => updateLink(id, { enabled: on });
  const featureLink = (id) => {
    setCfg((prev) => ({
      ...prev,
      links: prev.links.map((l) => ({ ...l, featured: l.id === id ? !l.featured : false })),
    }));
    setDirty(true);
  };
  const deleteLink = (id) => {
    setCfg((prev) => ({ ...prev, links: prev.links.filter((l) => l.id !== id) }));
    setDirty(true);
  };
  const addLink = () => {
    const newLink = {
      id: `new_${Date.now()}`,
      label: 'New Link',
      url: '#',
      description: '',
      icon: 'sparkles',
      category: 'Links',
      enabled: true,
      featured: false,
      order: (cfg.links?.length || 0),
      style: 'primary',
    };
    setCfg((prev) => ({ ...prev, links: [...(prev.links || []), newLink] }));
    setDirty(true);
  };

  // Drag & drop reorder
  const onDragStart = (id) => setDragging(id);
  const onDragOver = (id, e) => {
    if (e) e.preventDefault();
    setDragOver(id);
  };
  const onDrop = (targetId, e) => {
    if (e) e.preventDefault();
    if (!dragging || dragging === targetId) {
      setDragging(null); setDragOver(null); return;
    }
    setCfg((prev) => {
      const list = [...prev.links];
      const srcIdx = list.findIndex((l) => l.id === dragging);
      const dstIdx = list.findIndex((l) => l.id === targetId);
      if (srcIdx < 0 || dstIdx < 0) return prev;
      const [moved] = list.splice(srcIdx, 1);
      list.splice(dstIdx, 0, moved);
      return { ...prev, links: list };
    });
    setDragging(null); setDragOver(null); setDirty(true);
  };

  // Save
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/derwent-links/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(cfg),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      setCfg(saved);
      setDirty(false);
      savedRef.current = true;
      toast.success('Saved — /DerwentLinks is now live');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/DerwentLinks`;
    navigator.clipboard.writeText(url).then(() => toast.success('Copied to clipboard'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!cfg) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="derwent-links-admin">
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-center bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="dl-admin-back"
              aria-label="Back"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-600" /> Derwent Links
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Manage the public /DerwentLinks page — {cfg.links?.length || 0} links · Last saved {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : 'never'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="dl-admin-copy-url">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy public URL
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open('/DerwentLinks', '_blank')} data-testid="dl-admin-open-page">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open page
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              className={cn(dirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : '')}
              data-testid="dl-admin-save"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-slate-100 dark:bg-slate-800 w-fit mb-5">
          {[
            { key: 'editor', label: 'Editor', Icon: Settings },
            { key: 'analytics', label: 'Analytics', Icon: BarChart3 },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`dl-admin-tab-${key}`}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors',
                tab === key ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500',
              )}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === 'analytics' ? (
          <AnalyticsTab token={token} />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
            {/* Left column — editor */}
            <div className="space-y-5">
              {/* Profile */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm dark:text-white">Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px]">Name</Label>
                      <Input value={cfg.profile_name || ''} onChange={(e) => updateCfg({ profile_name: e.target.value })} data-testid="dl-admin-profile-name" />
                    </div>
                    <div>
                      <Label className="text-[11px]">Handle (@username)</Label>
                      <Input value={cfg.profile_handle || ''} onChange={(e) => updateCfg({ profile_handle: e.target.value })} placeholder="@derwentfinance" data-testid="dl-admin-profile-handle" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Headline / tagline</Label>
                    <Input value={cfg.profile_headline || ''} onChange={(e) => updateCfg({ profile_headline: e.target.value })} data-testid="dl-admin-profile-headline" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Bio</Label>
                    <Textarea rows={2} value={cfg.profile_bio || ''} onChange={(e) => updateCfg({ profile_bio: e.target.value })} data-testid="dl-admin-profile-bio" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                      <Label className="text-[11px]">Logo URL</Label>
                      <Input value={cfg.logo_url || ''} onChange={(e) => updateCfg({ logo_url: e.target.value })} placeholder="https://..." data-testid="dl-admin-profile-logo" />
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                      <Switch checked={!!cfg.verified} onCheckedChange={(v) => updateCfg({ verified: v })} data-testid="dl-admin-verified-toggle" />
                      <Label className="text-[11px] font-medium">Verified badge</Label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Hero image URL (optional — full-width photo at top)</Label>
                    <Input value={cfg.hero_image_url || ''} onChange={(e) => updateCfg({ hero_image_url: e.target.value })} placeholder="https://... a wide lifestyle/brand photo" data-testid="dl-admin-hero-image" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px]">Contact phone</Label>
                      <Input value={cfg.contact_phone || ''} onChange={(e) => updateCfg({ contact_phone: e.target.value })} placeholder="0400 000 000" data-testid="dl-admin-contact-phone" />
                    </div>
                    <div>
                      <Label className="text-[11px]">Contact email</Label>
                      <Input value={cfg.contact_email || ''} onChange={(e) => updateCfg({ contact_email: e.target.value })} placeholder="hello@derwentfinance.com.au" data-testid="dl-admin-contact-email" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Banner block */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm dark:text-white flex items-center justify-between">
                    <span className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-500" /> Optional Banner / Campaign block</span>
                    <Switch
                      checked={!!cfg.banner?.enabled}
                      onCheckedChange={(v) => updateCfg({ banner: { ...(cfg.banner || {}), enabled: v } })}
                      data-testid="dl-admin-banner-toggle"
                    />
                  </CardTitle>
                </CardHeader>
                {cfg.banner?.enabled && (
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px]">Title</Label>
                        <Input value={cfg.banner.title || ''} onChange={(e) => updateCfg({ banner: { ...cfg.banner, title: e.target.value } })} data-testid="dl-admin-banner-title" />
                      </div>
                      <div>
                        <Label className="text-[11px]">Subtitle</Label>
                        <Input value={cfg.banner.subtitle || ''} onChange={(e) => updateCfg({ banner: { ...cfg.banner, subtitle: e.target.value } })} data-testid="dl-admin-banner-subtitle" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px]">CTA label</Label>
                        <Input value={cfg.banner.cta_label || ''} onChange={(e) => updateCfg({ banner: { ...cfg.banner, cta_label: e.target.value } })} data-testid="dl-admin-banner-cta-label" />
                      </div>
                      <div>
                        <Label className="text-[11px]">CTA URL</Label>
                        <Input value={cfg.banner.cta_url || ''} onChange={(e) => updateCfg({ banner: { ...cfg.banner, cta_url: e.target.value } })} data-testid="dl-admin-banner-cta-url" />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Links */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm dark:text-white flex items-center justify-between gap-2">
                    <span>Links ({cfg.links?.length || 0})</span>
                    <Button size="sm" onClick={addLink} data-testid="dl-admin-add-link">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add link
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(cfg.links || []).map((link) => (
                    <LinkRow
                      key={link.id}
                      link={link}
                      onChange={updateLink}
                      onDelete={deleteLink}
                      onToggle={toggleLink}
                      onFeature={featureLink}
                      onDragStart={onDragStart}
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                      isDragging={dragging === link.id}
                      isDragOver={dragOver === link.id && dragging && dragging !== link.id}
                    />
                  ))}
                  {(cfg.links || []).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-6">No links yet — click &quot;Add link&quot; to get started.</p>
                  )}
                </CardContent>
              </Card>

              {/* Socials */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm dark:text-white">Social media</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {SOCIAL_PLATFORMS.map(({ key, label, Icon }) => {
                    const s = (cfg.socials || []).find((x) => x.platform === key) || { platform: key, url: '', enabled: false };
                    return (
                      <div key={key} className="flex items-center gap-2" data-testid={`dl-admin-social-${key}`}>
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </div>
                        <Input
                          value={s.url || ''}
                          placeholder={`https://${key}.com/derwentfinance`}
                          onChange={(e) => {
                            const others = (cfg.socials || []).filter((x) => x.platform !== key);
                            updateCfg({ socials: [...others, { platform: key, url: e.target.value, enabled: !!e.target.value }] });
                          }}
                          className="text-sm"
                        />
                        <Switch
                          checked={!!s.enabled}
                          onCheckedChange={(v) => {
                            const others = (cfg.socials || []).filter((x) => x.platform !== key);
                            updateCfg({ socials: [...others, { ...s, enabled: v }] });
                          }}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Pixels */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm dark:text-white">Tracking Pixels</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-[11px]">Meta Pixel ID</Label>
                    <Input value={cfg.meta_pixel_id || ''} onChange={(e) => updateCfg({ meta_pixel_id: e.target.value })} placeholder="1234567890" data-testid="dl-admin-meta-pixel" />
                  </div>
                  <div>
                    <Label className="text-[11px]">TikTok Pixel ID</Label>
                    <Input value={cfg.tiktok_pixel_id || ''} onChange={(e) => updateCfg({ tiktok_pixel_id: e.target.value })} placeholder="ABC123..." data-testid="dl-admin-tiktok-pixel" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Google Analytics ID</Label>
                    <Input value={cfg.ga_measurement_id || ''} onChange={(e) => updateCfg({ ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="dl-admin-ga-id" />
                  </div>
                </CardContent>
              </Card>

              {/* Footer text */}
              <Card className="dark:bg-slate-900 dark:border-slate-800">
                <CardContent className="pt-4">
                  <Label className="text-[11px]">Footer text</Label>
                  <Input value={cfg.footer_text || ''} onChange={(e) => updateCfg({ footer_text: e.target.value })} data-testid="dl-admin-footer" />
                </CardContent>
              </Card>
            </div>

            {/* Right column — preview */}
            <PreviewPanel cfg={cfg} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DerwentLinksAdmin;
